import { getAnalyticsEvents } from './analyticsService';

export interface ExperimentVariant {
  variantName: string;
  users: number;
  conversions: number;
  conversionRate: number;
}

export interface Experiment {
  experimentName: string;
  variants: ExperimentVariant[];
  totalUsers: number;
  statisticalSignificance: number;
  startDate: Date;
  endDate?: Date;
  status: 'active' | 'completed';
}

export interface ExperimentationAnalytics {
  experiments: Experiment[];
  activeExperiments: number;
  completedExperiments: number;
}

export const getExperimentationAnalytics = async (
  days: number = 30
): Promise<ExperimentationAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days * 2);

    // Get A/B test events
    const abTestEvents = events.filter((e) =>
      e.eventName === 'ab_test_assigned' || e.eventName === 'ab_test_variant_viewed'
    );

    // Group by experiment and variant
    const experimentMap = new Map<string, {
      variants: Map<string, { users: Set<string>; conversions: Set<string> }>;
      startDate: Date;
    }>();

    abTestEvents.forEach((event) => {
      const experimentName = (event.properties.experiment_name as string) ||
                            (event.properties.test_name as string) ||
                            'unknown';
      const variant = (event.properties.variant as string) ||
                     (event.properties.variant_name as string) ||
                     'control';

      if (!experimentMap.has(experimentName)) {
        experimentMap.set(experimentName, {
          variants: new Map(),
          startDate: event.timestamp,
        });
      }

      const experiment = experimentMap.get(experimentName)!;
      if (!experiment.variants.has(variant)) {
        experiment.variants.set(variant, {
          users: new Set(),
          conversions: new Set(),
        });
      }

      const variantData = experiment.variants.get(variant)!;
      variantData.users.add(event.userId);

      // Check if this is a conversion (would need conversion event tracking)
      if (event.eventName.includes('conversion') || event.properties.converted === true) {
        variantData.conversions.add(event.userId);
      }
    });

    // Calculate experiment metrics
    const experiments: Experiment[] = [];

    experimentMap.forEach((experimentData, experimentName) => {
      const variants: ExperimentVariant[] = [];
      let totalUsers = 0;

      experimentData.variants.forEach((variantData, variantName) => {
        const users = variantData.users.size;
        const conversions = variantData.conversions.size;
        const conversionRate = users > 0 ? (conversions / users) * 100 : 0;

        totalUsers += users;
        variants.push({
          variantName,
          users,
          conversions,
          conversionRate,
        });
      });

      // Calculate statistical significance (simplified chi-square test)
      const statisticalSignificance = calculateSignificance(variants);

      experiments.push({
        experimentName,
        variants,
        totalUsers,
        statisticalSignificance,
        startDate: experimentData.startDate,
        status: 'active', // Would check end date if available
      });
    });

    const activeExperiments = experiments.filter((e) => e.status === 'active').length;
    const completedExperiments = experiments.filter((e) => e.status === 'completed').length;

    return {
      experiments: experiments.sort((a, b) => b.totalUsers - a.totalUsers),
      activeExperiments,
      completedExperiments,
    };
  } catch (error) {
    console.error('Error calculating experimentation analytics:', error);
    throw error;
  }
};

function calculateSignificance(variants: ExperimentVariant[]): number {
  // Simplified chi-square test for statistical significance
  // In production, use proper statistical libraries
  if (variants.length < 2) return 0;

  const totalUsers = variants.reduce((sum, v) => sum + v.users, 0);
  const totalConversions = variants.reduce((sum, v) => sum + v.conversions, 0);
  const overallRate = totalUsers > 0 ? totalConversions / totalUsers : 0;

  if (totalUsers === 0 || overallRate === 0) return 0;

  // Simplified calculation
  let chiSquare = 0;
  variants.forEach((variant) => {
    const expectedConversions = variant.users * overallRate;
    const expectedNonConversions = variant.users * (1 - overallRate);
    const observedConversions = variant.conversions;
    const observedNonConversions = variant.users - variant.conversions;

    if (expectedConversions > 0) {
      chiSquare += Math.pow(observedConversions - expectedConversions, 2) / expectedConversions;
    }
    if (expectedNonConversions > 0) {
      chiSquare += Math.pow(observedNonConversions - expectedNonConversions, 2) / expectedNonConversions;
    }
  });

  // Convert to p-value approximation (simplified)
  // In production, use proper statistical distribution
  const pValue = Math.max(0, 1 - (chiSquare / 100));
  return pValue * 100; // Return as percentage
}

